import type { CoreEvent } from '../../core/types';

/** Ce que le cœur déclenche côté son : un effet (clé de `manifest.audio.sfx`) ou un changement de musique. */
export type Cue = { kind: 'sfx'; key: string } | { kind: 'music'; key: 'incarnam' | 'boss' };
export interface CueContext { playerId: number | null; bossRoom: boolean; }

/** Son de lancer par sort ; `null` = silencieux (la téléportation sonne à l'événement `blink`). */
export const SPELL_SOUND: Record<string, string | null> = {
  'rayon-obscur': 'spell-rayon', aiguille: 'spell-aiguille', gelure: 'spell-gelure', sablier: 'spell-sablier',
  fletrissement: 'spell-fletrissement', 'poussiere-temporelle': 'spell-poussiere', contre: 'spell-contre', teleportation: null,
};

const DEATH_RULES: [RegExp, string][] = [
  [/^(petit-|chef-)?bouftou(-royal)?$/, 'die-bouftou'],
  [/^boufton-/, 'die-boufton'],
  [/^larve-/, 'die-larve'],
  [/^(piou-|petit-tofu$|tofu$)/, 'die-tofu'],
  [/^arakne/, 'die-arakne'],
  [/^chafer/, 'die-chafer'],
  [/^crabe/, 'die-crabe'],
  [/^moskito/, 'die-moskito'],
  [/^sanglier/, 'die-sanglier'],
  [/^pissenlit/, 'die-pissenlit'],
  [/^(rose-demoniaque|tournesol|champ-champ)/, 'die-plante'],
  [/^milimilou/, 'die-milimilou'],
];

/** Son de mort d'un monstre selon sa famille (id de définition) ; `die-generic` sinon. */
export function deathSound(defId: string): string {
  for (const [re, key] of DEATH_RULES) if (re.test(defId)) return key;
  return 'die-generic';
}

const sfx = (key: string): Cue => ({ kind: 'sfx', key });

/** Sons à jouer pour un événement du cœur. Pur : aucune dépendance à Phaser. */
export function cuesForEvent(e: CoreEvent, ctx: CueContext): Cue[] {
  switch (e.type) {
    case 'damage': return [sfx(e.targetId === ctx.playerId ? 'player-hit' : 'monster-hit')];
    case 'death': return e.entityId === ctx.playerId ? [] : [sfx(deathSound(e.defId))];
    case 'pickup': return [sfx(e.pickupType === 'kamas' ? 'pickup-kamas' : 'pickup-xp')];
    case 'levelUp': return [sfx('level-up')];
    case 'spellCast': { const k = SPELL_SOUND[e.spellId]; return k === null ? [] : [sfx(k ?? 'spell-generic')]; }
    case 'zoneHit': return [sfx('zone-hit')];
    case 'blink': return [sfx('teleport')];
    case 'waveStart': return e.wave === 1 ? (ctx.bossRoom ? [sfx('boss-appear')] : []) : [sfx('wave-start')];
    case 'roomCleared': return [sfx('room-cleared')];
    case 'playerDied': return [sfx('player-death')];
    case 'heal': return e.amount > 0 && e.source !== 'lifesteal' && e.source !== 'summon' ? [sfx('heal')] : []; // vol de vie et invocations soignent en rafale : silence
    case 'itemGranted': return [sfx('chest')];
    case 'cardChosen': return [sfx('ui-click')];
    case 'doorsOffered': return [sfx('wave-start')];
    case 'roomEntered': return [...(e.roomIndex > 0 ? [sfx('door-pass')] : []), { kind: 'music', key: ctx.bossRoom ? 'boss' : 'incarnam' }];
    case 'shopOpened': return [sfx('shop-open')];
    case 'bought': return [sfx('buy')];
    case 'bossCharge': return [sfx('boss-charge')];
    case 'runEnded': return e.outcome === 'victory' ? [sfx('victory')] : [];
    case 'invalidCommand': return [sfx('error')];
    default: return [];
  }
}

/** Suit la salle courante (événements `roomEntered`) pour savoir si l'on est dans la salle du boss : la dernière de l'étage. */
export class EventCues {
  private roomIndex = 0;
  cuesFor(events: CoreEvent[], info: { playerId: number | null; roomsTotal: number }): Cue[] {
    const out: Cue[] = [];
    for (const e of events) {
      if (e.type === 'roomEntered') this.roomIndex = e.roomIndex;
      out.push(...cuesForEvent(e, { playerId: info.playerId, bossRoom: this.roomIndex === info.roomsTotal - 1 }));
    }
    return out;
  }
  reset(): void { this.roomIndex = 0; }
}
